
import React, { useEffect, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { makeStyles } from "@material-ui/core/styles"
// Internal
const CONSTS = require("shared/consts")
import { makeStepsMatchABI } from "shared/get-paramters-of-deployerc20-step"
import NonInteractive from "./non-interactive"
import useAsyncEffect from "../utils/use-async-effect"
import useIsLoggedIn from "../utils/use-is-logged-in"
import isEditableTest from "../../../shared/is-editable-test"
// @ts-ignore
import { requestJSON } from "../utils/request-module"
import { useLocalStorage } from "../utils/use-local-storage"
import { track } from "../utils/tracking"
import "../utils/translate"
import useIsWide from "../utils/use-is-wide"
import TermsModal from "../components/terms-modal"

const PAGES = {
  interactive: "interactive",
  noninteractive: "noninteractive",
}

export default function Home({ setIsSubmitLoading, isLoading, scanningCancelled }) {
  const { t } = useTranslation()
  const isWideScreen = useIsWide()

  const title = 'Automatic testing tool';
  const description = (isWideScreen) ? t("token_and_smart_contract_testing_intro_start_multifile"):t("token_and_smart_contract_testing_intro_start");

  var accepted = true;
  
  if(window["config"].SKIP_AUTOMATIC_TESTING_TERMS != 'true'){
    accepted = localStorage.SKIP_AUTOMATIC_TESTING_TERMS === 'true';    
  }
 
  const [isTermsAccepted, setTermsAccepted] = useState(
    true
  )

    const HOME_URL = window["config"].HOME_URL ?? "https://blockchain.ey.com"
    
    // State and Hooks (state managed by hooks)
    var [mainFile, setMainFile] = useLocalStorage("mainfile", "")
    var [mainContract, setMainContract] = useLocalStorage("mainContract", "")
  
    const { isLoggedIn, userInfo } = useIsLoggedIn()
  
    const [compilerVersion, setCompilerVersion] = useLocalStorage(
      "compilerVersion",
      "",
    )
    const [compilerSettings, setCompilerSettings] = useLocalStorage(
      "compilerSettings",
      {},
    )
    const [ABI, setABI] = useLocalStorage("ABI")
    const [files, setFiles] = useLocalStorage(
      "files",
      CONSTS.FILE_EXPLORER_EMPTY_FILES_LIST,
    )
  
    const [importPopoverAnchorEl, setImportPopoverAnchorEl] = useState(null)
  
    const defaultResponse = { test_results: [], errors: [] }
    const [nonInteractiveResponse, setNonInteractiveResponse] = useState(
      defaultResponse,
    )
  
    const [steps, setSteps] = useLocalStorage("steps", [])

    useEffect(() => {
      track("View SCR home page")
    }, [])

    /** @type {import("../../../backend/interfaces").Test}*/
    var emptyTest
    var [selectedTest, __setSelectedTest] = useState(emptyTest)
    var [page, setPage] = useState(PAGES.noninteractive)
  
    /** @param test {import("../../../backend/interfaces").Test}*/
    function setSelectedTest(test) {
      __setSelectedTest(test)
      setSteps(
        makeStepsMatchABI(
          files,
          mainFile,
          mainContract,
          compilerVersion,
          test.steps,
          ABI,
        ),
      )
    }

    const onLeavePage = (e = window.event) => {
      e.returnValue = t("are_you_sure_changes_not_saved") // browser support for: Gecko + IE
      return t("are_you_sure_changes_not_saved") // browser support for: Safari, Chrome, and other WebKit-derived browsers
    }

    const leavePageHandler = useCallback(onLeavePage, [])

    const addLeavePageHandlers = () => {
      window.addEventListener("beforeunload", leavePageHandler)
      window.addEventListener("blur", () => {
        window.removeEventListener("beforeunload", leavePageHandler)
      })
    }

    const removeLeavePageHandlers = () => {
      window.removeEventListener("beforeunload", leavePageHandler)
    }
  
  
    // Variables
    const useStyles = makeStyles(theme => ({
      terms: {
        padding: "2vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      app: {
        //@ts-ignore lightest not exists.
        backgroundColor: theme.palette.primary.lightest,
        minHeight: "100vh",
      },
    }))
    const classes = useStyles({})
  
    /** @type {import("../../../backend/interfaces").Test[]}*/
    var emptyTestsArray
    var [tests, setTests] = useState(emptyTestsArray)
  
    useAsyncEffect(async () => {
      /** @type {import("../../../backend/interfaces").Test[]}*/
      var tests = await requestJSON("GET", "/list-of-tests")
      tests = tests.filter(isEditableTest)
      setTests(tests)
    })
  
    function clickNewCustomTest() {
      setPage(PAGES.interactive)
      //@ts-ignore
      setSelectedTest({
        steps: [],
      })
    }
    const [customTests, setCustomTests] = useState([])
    function deleteCustomTest(index) {
      if (!confirm("Are you sure you would like to delete the test?")) return
      var tests = JSON.parse(localStorage.tests || "[]")
      tests.splice(index, 1)
      localStorage.tests = JSON.stringify(tests)
      setCustomTests(tests)
    }
    function clickCustomTest(index) {
      var tests = JSON.parse(localStorage.tests || "[]")
      var test = tests[index]
      setSelectedTest({ ...test })
      setPage(PAGES.interactive)
    }
  
    useEffect(() => {
      setCustomTests(JSON.parse(localStorage.tests || "[]"))
    }, [localStorage.tests])
  
    async function submitCode() {
      setPage(PAGES.noninteractive)
      try {
        setIsSubmitLoading(true)
        setNonInteractiveResponse(defaultResponse)
        if (!mainFile) {
          mainFile = "main.sol"
          setMainFile("main.sol")
        }
        /** @type {import("../../../backend/interfaces").postTestBody} */
        var request = {
          files,
          mainContract,
          mainFile,
          solVersion: compilerVersion,
          compilerSettings
        }
        /** @type {{test_results:[],errors:[],abi}} */
        const response = await requestJSON("POST", "/test", request)
        if (scanningCancelled.current) {
          // In case scanning was cancelled
          scanningCancelled.current = false
          return
        }
        if (response.abi) setABI?.(response.abi)
        setIsSubmitLoading(false)
        setNonInteractiveResponse(response)
      } catch (err) {
        /**/
        setIsSubmitLoading(false)
      }
      addLeavePageHandlers()
    }
  
    const pageClickHandler = (e) => {
      if (!importPopoverAnchorEl) {
        return
      }
      const isClickOnToggleButtons = Boolean(e?.target?.closest("[id*='inputSelectionButton'"))
      if (!isClickOnToggleButtons) {
        setImportPopoverAnchorEl(null)
      }
    }

    const redirectToProductPage = () => {
      window.location.href = HOME_URL+'/products/review-tool'
    }

    const termsAccepted = () =>{
      localStorage.setItem('SKIP_AUTOMATIC_TESTING_TERMS', 'true')
      setTermsAccepted(true);
    }
  return (
    <div className={classes.app} onClick={pageClickHandler}>
      {isTermsAccepted && (
        page == PAGES.noninteractive && (
          <NonInteractive
            selectedContract
            setSelectedContract
            deleteCustomTest={deleteCustomTest}
            customTests={customTests}
            submitCode={submitCode}
            isLoading={isLoading}
            compilerSettings={compilerSettings}
            setCompilerSettings={setCompilerSettings}
            response={nonInteractiveResponse}
            clickNewCustomTest={clickNewCustomTest}
            compilerVersion={compilerVersion}
            setCompilerVersion={setCompilerVersion}
            clickCustomTest={clickCustomTest}
            files={files}
            setFiles={setFiles}
            mainContract={mainContract}
            setMainFile={setMainFile}
            mainFile={mainFile}
            setMainContract={setMainContract}
            importPopoverAnchorEl={importPopoverAnchorEl}
            setImportPopoverAnchorEl={setImportPopoverAnchorEl}
            setABI={setABI}
            addLeavePageHandlers={addLeavePageHandlers}
            removeLeavePageHandlers={removeLeavePageHandlers}
            title={title}
            description={description}
          />
        )
      )}
      {!isTermsAccepted && (
        <TermsModal 
          open = {true}
          onClose = {redirectToProductPage}
          onAccepted={termsAccepted}
        />
        )}
    </div>
  )
}
